<template>
  <tr class="purchase-addon-selector-row" v-if="!isMobile">
    <td class="py-9">
      <div class="font-weight-medium">{{ addonConfig.name }}</div>
      <div class="text-caption text-grey-darken-3">
        {{ addonConfig.priceDescription }}
      </div>
    </td>
    <td class="purchase-addon-description py-9 text-body-2">
      {{ addonConfig.description }}
    </td>
    <td v-if="addonConfig.hasQuantity" class="text-center py-9">
      <quantity-input
        :value="localQuantity"
        @update="(v) => setQuantity(v)"
        :loading="service.setAddonQuantity.isLoading"
        :size="resolveSmallComponentSize"
        :aria-label="`${addonConfig.name} quantity`"
        :minus-aria-label="`Remove one ${addonConfig.name}`"
        :plus-aria-label="`Add one ${addonConfig.name}`"
      ></quantity-input>

      <v-expand-transition>
        <div
          v-if="service.setAddonQuantity.message"
          class="text-caption text-error"
          role="alert"
        >
          {{ service.setAddonQuantity.message }}
        </div>
      </v-expand-transition>
    </td>
    <td v-else class="text-center py-9">
      <v-btn
        v-if="serverQuantity == 0"
        color="primary"
        :size="resolveSmallComponentSize"
        @click="setQuantity(1)"
        :loading="service.setAddonQuantity.isLoading"
      >
        Add
      </v-btn>
      <v-btn
        v-else
        color="primary"
        variant="outlined"
        :size="resolveSmallComponentSize"
        @click="setQuantity(0)"
        :loading="service.setAddonQuantity.isLoading"
      >
        Remove
      </v-btn>

      <v-expand-transition>
        <div
          v-if="service.setAddonQuantity.message"
          class="text-caption text-error mt-1"
          role="alert"
        >
          {{ service.setAddonQuantity.message }}
        </div>
      </v-expand-transition>
    </td>
    <td class="text-right py-9">
      <v-fade-transition mode="out-in">
        <v-skeleton-loader
          key="subtotal-skele"
          v-if="service.setAddonQuantity.isLoading"
          type="text"
          class="d-inline-block"
          width="60"
        />
        <div v-else key="subtotal-value">{{ subtotal }}</div>
      </v-fade-transition>
    </td>
  </tr>
  <!-- Mobile Card Layout -->
  <v-card class="addon-card" variant="flat" v-else>
    <div class="text-h6">{{ addonConfig.name }}</div>
    <div class="text-caption text-grey-darken-3">
      {{ addonConfig.description }}
    </div>
    <div
      class="text-caption text-grey-darken-3 pt-1"
      v-if="addonConfig.priceDescription"
    >
      {{ addonConfig.priceDescription }}
    </div>

    <!-- Quantity Controls Row -->
    <div class="quantity-row">
      <!-- Quantity Input for items with quantity -->
      <quantity-input
        v-if="addonConfig.hasQuantity"
        :value="localQuantity"
        @update="(v) => setQuantity(v)"
        :loading="service.setAddonQuantity.isLoading"
        :size="resolveSmallComponentSize"
      />

      <!-- Add/Remove Button for items without quantity -->
      <div v-else class="addon-button-container">
        <v-btn
          v-if="serverQuantity == 0"
          color="primary"
          :size="resolveSmallComponentSize"
          @click="setQuantity(1)"
          :loading="service.setAddonQuantity.isLoading"
        >
          Add
        </v-btn>
        <v-btn
          v-else
          color="primary"
          variant="outlined"
          :size="resolveSmallComponentSize"
          @click="setQuantity(0)"
          :loading="service.setAddonQuantity.isLoading"
        >
          Remove
        </v-btn>
      </div>

      <!-- Subtotal -->
      <v-fade-transition mode="out-in">
        <v-skeleton-loader
          v-if="service.setAddonQuantity.isLoading"
          type="text"
        />
        <div v-else class="subtotal-value-mobile">
          {{ subtotal }}
        </div>
      </v-fade-transition>
    </div>

    <!-- Error Messages -->
    <v-expand-transition>
      <div
        v-if="service.setAddonQuantity.message"
        class="text-caption text-error mt-2"
        role="alert"
      >
        {{ service.setAddonQuantity.message }}
      </div>
    </v-expand-transition>
  </v-card>
</template>

<style lang="scss">
@media screen and (max-width: 370px) {
  .addon-card {
    .quantity-row {
      flex-direction: column;
    }
  }
}

@media screen and (max-width: 960px) {
  .addon-card {
    padding: 6px 8px !important;
    margin: 4px 2px !important;
    border-radius: 4px;
    box-shadow: none !important;
    background: #f8f9fa;

    .text-h6 {
      font-size: inherit !important;
      line-height: inherit !important;
      font-weight: 500;
      margin-bottom: 1px !important;
      color: #212529;
    }

    .text-caption {
      font-size: 0.8rem !important;
      line-height: 1.1;
      color: #6c757d;
      margin-bottom: 0 !important;
    }

    .quantity-row {
      margin-top: 4px;
      margin-bottom: 1px;
      min-height: 24px;
    }

    .subtotal-value-mobile {
      font-size: inherit !important;
      font-weight: 600;
      line-height: inherit !important;
      text-align: right;
      color: #212529;

      .v-skeleton-loader {
        width: 40px;
        min-height: 14px;
        .v-skeleton-loader__text {
          height: 10px;
        }
      }
    }

    .quantity-input {
      &.quantity-input--small {
        .v-input__control {
          width: 32px !important;
          min-width: 32px;
        }
        input {
          font-size: 0.7rem !important;
          padding: 1px 0;
        }
        .v-btn {
          --v-btn-height: 20px !important;
          min-width: 20px !important;
          width: 20px !important;
          height: 20px !important;
          .v-icon {
            font-size: 0.6rem !important;
          }
        }
      }
    }

    .v-skeleton-loader {
      min-height: 14px !important;
      .v-skeleton-loader__text {
        height: 10px;
      }
    }
  }
}
</style>
<script setup lang="ts">
import { EventAddonDto } from "@/models.g";
import {
  PublicPurchaseServiceViewModel,
  TicketPurchaseDtoViewModel,
} from "@/viewmodels.g";
import { formatCurrency, setupPurchaseReloadHandling } from "@common/utils";
import { computed, ref } from "vue";
import { useDisplay } from "vuetify";
import useAccessibility from "@common/composables/useAccessibility";

const props = withDefaults(
  defineProps<{
    purchase: TicketPurchaseDtoViewModel;
    addonConfig: EventAddonDto;
    isMobile?: boolean;
  }>(),
  {
    isMobile: false,
  },
);
const display = useDisplay();
const { resolveSmallComponentSize } = useAccessibility();

const service = new PublicPurchaseServiceViewModel();
const isMobile = computed(() => display.smAndDown.value);

/** The current quantity of the item according to the last data returned from the server. */
const serverQuantity = computed(() => {
  return items.value.length;
});

/** The current quantity desired/inputted by the user. */
const localQuantity = ref(0);
function refreshLocalQuantity() {
  localQuantity.value = items.value.length;
}

const items = computed(() => {
  return props.purchase.addons!.filter(
    (t) => t.addonItemConfigurationId == props.addonConfig.configurationId,
  );
});

const subtotal = computed(() => {
  return formatCurrency(
    items.value.reduce((prev, cur) => prev + cur.price!, 0),
  );
});

async function setQuantity(input: string | number) {
  var num = parseInt(input.toString());
  localQuantity.value = num;

  try {
    await service.setAddonQuantity(
      props.purchase.publicId,
      props.addonConfig.configurationId,
      num,
    );
  } catch (err) {
    console.error(err);

    // Always refresh the local quantities if there is an error.
    refreshLocalQuantity();
  }
}

service.setAddonQuantity.setConcurrency("debounce");
setupPurchaseReloadHandling(props.purchase, service.setAddonQuantity);

refreshLocalQuantity();
</script>
